import { useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Sparkles, ClipboardList, CheckCircle2, AlertTriangle,
  Phone, Plus, Trash2, Save, Download, Link2,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Plano de Cuidados MINIMAL para serviços pontuais.
 *
 * Renderiza apenas quando `lead.qualification.service.continuity === "pontual"`.
 * Exige que a RT já esteja realizada / agendada — não fazemos plano de
 * cuidados de um pontual antes da conversa técnica.
 *
 * Fluxo:
 *   1. Consultor clica "Gerar sugestões IA" (POST .../generate)
 *   2. IA devolve 3 checklists (activities/priorities/escalation) todas
 *      com `included: false`. O Consultor tica as que se aplicam.
 *   3. Consultor edita/adiciona items manualmente e grava (PATCH).
 *   4. "Aprovar plano" finaliza (data + user).
 *
 * data-testid: pontual-care-plan-block
 */
const SECTION_META = {
  activities: {
    icon: ClipboardList,
    title: "Actividades a executar",
    sub: "Marca só as que se aplicam a este serviço",
    color: "text-[hsl(var(--success))]",
  },
  priorities: {
    icon: AlertTriangle,
    title: "Pontos de atenção",
    sub: "O que deve estar sempre à vista",
    color: "text-[hsl(var(--warning))]",
  },
  escalation: {
    icon: Phone,
    title: "Quando escalar",
    sub: "Cenários em que se contacta gestor / familiar",
    color: "text-[hsl(var(--danger))]",
  },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function PontualCarePlanBlock({ lead, onChanged }) {
  const [busyGen, setBusyGen] = useState(false);
  const [busySave, setBusySave] = useState(false);

  const isPontual = ((lead?.qualification?.service?.continuity) || "").toLowerCase() === "pontual";
  const rtReady = Boolean(lead?.scheduled_qt_at)
                   || ["qt_realizada", "proposta_enviada", "aceite"].includes(lead?.status);

  const [plan, setPlan] = useState(lead?.pontual_care_plan || null);
  const [dirty, setDirty] = useState(false);

  // Local edit helpers ------------------------------------------------------
  const _mut = (mutator) => {
    setPlan((prev) => {
      const next = JSON.parse(JSON.stringify(prev || { objectives: [], activities: [], priorities: [], escalation: [] }));
      mutator(next);
      return next;
    });
    setDirty(true);
  };

  const toggleItem = (section, idx) => {
    _mut((n) => {
      const it = n[section]?.[idx];
      if (it) it.included = !it.included;
    });
  };

  const setItemText = (section, idx, text) => {
    _mut((n) => {
      const it = n[section]?.[idx];
      if (it) it.text = text;
    });
  };

  const removeItem = (section, idx) => {
    _mut((n) => { n[section].splice(idx, 1); });
  };

  const addItem = (section) => {
    _mut((n) => { n[section].push({ text: "", included: true, notes: null }); });
  };

  const setObjective = (idx, text) => {
    _mut((n) => { n.objectives[idx] = text; });
  };

  const removeObjective = (idx) => {
    _mut((n) => { n.objectives.splice(idx, 1); });
  };

  const addObjective = () => {
    _mut((n) => { n.objectives.push(""); });
  };

  // API -----------------------------------------------------------------
  const generateAI = async () => {
    if (busyGen) return;
    if (plan && !window.confirm("Já existe um plano — as sugestões actuais serão substituídas pelas novas. Continuar?")) return;
    setBusyGen(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/care-plan/pontual/generate`);
      setPlan(data);
      setDirty(false);
      toast.success("Plano pontual gerado pela IA");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falhou a gerar plano");
    } finally {
      setBusyGen(false);
    }
  };

  const savePlan = async (approve = null) => {
    if (busySave || !plan) return;
    setBusySave(true);
    try {
      const payload = {
        objectives: (plan.objectives || []).filter((s) => (s || "").trim()),
        activities: (plan.activities || []).filter((i) => (i.text || "").trim()),
        priorities: (plan.priorities || []).filter((i) => (i.text || "").trim()),
        escalation: (plan.escalation || []).filter((i) => (i.text || "").trim()),
      };
      if (approve !== null) payload.approved = approve;
      const { data } = await api.patch(`/leads/${lead.id}/care-plan/pontual`, payload);
      setPlan(data);
      setDirty(false);
      toast.success(approve === true ? "Plano aprovado" : "Plano guardado");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falhou a guardar");
    } finally {
      setBusySave(false);
    }
  };

  // Render guards -----------------------------------------------------------
  if (!isPontual) return null;
  if (!rtReady) return null;

  const isApproved = Boolean(plan?.approved_at);
  const includedCount = (plan?.activities || []).filter((i) => i.included).length
                        + (plan?.priorities || []).filter((i) => i.included).length
                        + (plan?.escalation || []).filter((i) => i.included).length;

  return (
    <div
      data-testid="pontual-care-plan-block"
      className="rounded-2xl border border-border bg-card p-5"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isApproved ? "bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/40" : "bg-primary/10 border border-primary/30"}`}>
          <ClipboardList className={`w-5 h-5 ${isApproved ? "text-[hsl(var(--success))]" : "text-primary"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.15em] font-semibold ${isApproved ? "text-[hsl(var(--success))]" : "text-primary"} mb-0.5`}>
            Plano de Cuidados · Pontual
          </div>
          <h3 className="font-ui text-lg text-foreground leading-tight">
            {plan ? (isApproved ? "Plano aprovado" : "Rever e ticar itens aplicáveis") : "Sem plano ainda"}
          </h3>
          {plan?.generated_at && (
            <div className="text-[11px] text-muted-foreground mt-1">
              Gerado por <b>{plan.generated_by_name || "IA"}</b> em {fmtDate(plan.generated_at)}
              {plan.updated_at && plan.updated_at !== plan.generated_at && (
                <> · editado {fmtDate(plan.updated_at)} por {plan.updated_by_name || "—"}</>
              )}
              {isApproved && (
                <> · <span className="text-[hsl(var(--success))] font-semibold">Aprovado {fmtDate(plan.approved_at)} por {plan.approved_by_name}</span></>
              )}
            </div>
          )}
        </div>
        <button
          onClick={generateAI}
          disabled={busyGen}
          data-testid="btn-generate-pontual-cp"
          className="inline-flex items-center gap-1.5 text-xs bg-foreground hover:bg-foreground/90 text-background rounded-full px-4 py-2 font-semibold disabled:opacity-50 shrink-0"
        >
          {busyGen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {plan ? "Regenerar" : "Gerar sugestões IA"}
        </button>
      </div>

      {!plan && (
        <div className="text-sm text-muted-foreground bg-muted/40 border border-border rounded-xl px-4 py-6 text-center">
          A IA lê a qualificação inicial + notas da RT deste lead e sugere uma
          <b> checklist enxuta</b> (objectivos + actividades + pontos de atenção +
          cenários de escalar). Depois tu ticas o que se aplica e aprovas.
        </div>
      )}

      {plan && (
        <div className="space-y-5">
          {/* Objectives */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-ui text-sm text-foreground uppercase tracking-widest">
                Objectivos do serviço
              </h4>
              <button onClick={addObjective} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Adicionar
              </button>
            </div>
            <div className="space-y-2">
              {(plan.objectives || []).length === 0 && (
                <div className="text-xs text-muted-foreground italic">(sem objectivos definidos)</div>
              )}
              {(plan.objectives || []).map((obj, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={obj}
                    onChange={(e) => setObjective(i, e.target.value)}
                    data-testid={`pontual-cp-obj-${i}`}
                    className="flex-1 bg-muted/40 border border-border rounded-lg px-3 py-1.5 text-sm"
                  />
                  <button onClick={() => removeObjective(i)} className="text-muted-foreground/70 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Checklists */}
          {["activities", "priorities", "escalation"].map((section) => {
            const meta = SECTION_META[section];
            const Icon = meta.icon;
            const items = plan[section] || [];
            return (
              <div key={section} data-testid={`pontual-cp-section-${section}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className={`font-ui text-sm uppercase tracking-widest ${meta.color} flex items-center gap-1.5`}>
                      <Icon className="w-3.5 h-3.5" /> {meta.title}
                    </h4>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{meta.sub}</div>
                  </div>
                  <button
                    onClick={() => addItem(section)}
                    data-testid={`pontual-cp-add-${section}`}
                    className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Adicionar
                  </button>
                </div>
                <div className="space-y-1.5">
                  {items.length === 0 && (
                    <div className="text-xs text-muted-foreground italic">(sem itens)</div>
                  )}
                  {items.map((it, idx) => (
                    <label
                      key={idx}
                      className={`flex items-start gap-3 rounded-lg border px-3 py-2 transition-colors cursor-pointer ${it.included ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25" : "bg-muted/40 border-border hover:bg-card"}`}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(it.included)}
                        onChange={() => toggleItem(section, idx)}
                        data-testid={`pontual-cp-check-${section}-${idx}`}
                        className="mt-0.5 w-4 h-4 rounded border-border text-[hsl(var(--success))] focus:ring-emerald-500"
                      />
                      <input
                        type="text"
                        value={it.text || ""}
                        onChange={(e) => setItemText(section, idx, e.target.value)}
                        data-testid={`pontual-cp-text-${section}-${idx}`}
                        className={`flex-1 bg-transparent text-sm outline-none border-0 p-0 ${it.included ? "text-foreground" : "text-muted-foreground"}`}
                      />
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); removeItem(section, idx); }}
                        className="text-muted-foreground/70 hover:text-red-500 shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Footer bar */}
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-border">
            <div className="text-[11px] text-muted-foreground">
              <b>{includedCount}</b> itens ticados
              {dirty && <span className="ml-2 text-[hsl(var(--warning))] font-semibold">· alterações por gravar</span>}
            </div>
            <div className="flex items-center gap-2">
              {/* Descarregar PDF — só disponível quando o plano existe.
                  Se ainda não estiver aprovado o PDF inclui um aviso
                  "RASCUNHO" no topo. Chamado via axios para que o JWT
                  seja anexado (endpoint tem require_roles). */}
              <button
                type="button"
                onClick={async () => {
                  try {
                    const resp = await api.get(
                      `/leads/${lead.id}/care-plan/pontual/pdf`,
                      { responseType: "blob" },
                    );
                    const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
                    const cared = (lead?.qualification?.cared_person?.name)
                                    || (lead?.qualification?.contact?.name) || "cliente";
                    const safe = cared.replace(/[^A-Za-z0-9._-]+/g, "_");
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `PlanoPontual_${safe}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Falhou ao gerar PDF");
                  }
                }}
                data-testid="btn-pontual-cp-pdf"
                className="inline-flex items-center gap-1.5 text-xs bg-card hover:bg-muted/40 border border-border text-foreground rounded-full px-4 py-2 font-semibold"
                title="Descarregar PDF minimal para partilhar com o cuidador"
              >
                <Download className="w-3.5 h-3.5" /> PDF
              </button>
              {isApproved && plan?.public_token && (
                <button
                  type="button"
                  onClick={() => {
                    const url = `${window.location.origin}/care-plan/pontual/${plan.public_token}`;
                    navigator.clipboard.writeText(url)
                      .then(() => toast.success("Link público copiado — partilhe com o cuidador/cliente"))
                      .catch(() => toast.error("Não foi possível copiar automaticamente. Link: " + url));
                  }}
                  data-testid="btn-pontual-cp-copy-link"
                  className="inline-flex items-center gap-1.5 text-xs bg-card hover:bg-muted/40 border border-border text-foreground rounded-full px-4 py-2 font-semibold"
                  title="Copiar link para partilhar com Ajudante Familiar / cliente"
                >
                  <Link2 className="w-3.5 h-3.5" /> Copiar link
                </button>
              )}
              {isApproved && (
                <button
                  onClick={() => savePlan(false)}
                  disabled={busySave}
                  data-testid="btn-pontual-cp-reopen"
                  className="text-xs bg-card hover:bg-muted/40 border border-border text-foreground rounded-full px-4 py-2 font-semibold disabled:opacity-50"
                >
                  Reabrir edição
                </button>
              )}
              {!isApproved && (
                <>
                  <button
                    onClick={() => savePlan(null)}
                    disabled={busySave || !dirty}
                    data-testid="btn-pontual-cp-save"
                    className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-4 py-2 font-semibold disabled:opacity-50"
                  >
                    {busySave ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Guardar
                  </button>
                  <button
                    onClick={() => savePlan(true)}
                    disabled={busySave || includedCount === 0}
                    data-testid="btn-pontual-cp-approve"
                    title={includedCount === 0 ? "Tique pelo menos um item antes de aprovar" : undefined}
                    className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-primary-foreground rounded-full px-4 py-2 font-semibold disabled:opacity-50"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Aprovar plano
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
